import React, { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";

import { Button } from "@/components/primitives/Button";
import { getVersion } from "@/api/version";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/theme";

interface HeaderProps {
  action?: React.ReactNode;
}

const navItems = [
  { label: "Status", href: "/" },
  { label: "Flows", href: "/flows" },
  { label: "Dashboard", href: "/dashboard" },
  { label: "Actions", href: "/actions" },
  { label: "Connections", href: "/connections" },
];

export const Header: React.FC<HeaderProps> = ({ action }) => {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [version, setVersion] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getVersion()
      .then((info) => {
        if (!cancelled && typeof info.version === "string") setVersion(info.version);
      })
      .catch(() => {
        // Version metadata is optional; an unavailable API must not break navigation.
      });
    return () => { cancelled = true; };
  }, []);

  const logoSrc = theme === "dark" ? "/logo-navbar-dark.svg" : "/logo-navbar-light.svg";

  return (
    <header className="glass sticky top-0 z-40">
      <div className="page-container flex h-20 items-center justify-between">
        <a href="/" className="flex min-w-0 flex-col items-start">
          <img src={logoSrc} alt="Service Health Monitor" className="h-9" />
          {version && <span className="text-xs text-slate-500 dark:text-slate-400" aria-label="Application version">v{version}</span>}
        </a>

        <div className="flex items-center gap-1 sm:gap-2">
          {user &&
            navItems.map((item) => (
              <Button
                key={item.href}
                variant="ghost"
                size="sm"
                onClick={() => (window.location.href = item.href)}
                className="hidden md:inline-flex"
              >
                {item.label}
              </Button>
            ))}

          {action && <div className="ml-2 flex items-center gap-2">{action}</div>}

          <button
            type="button"
            onClick={toggle}
            className="ml-2 inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-600 transition-colors hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {user && (
            <div className="ml-2 hidden items-center gap-2 pl-2 sm:flex sm:border-l sm:border-slate-200 dark:sm:border-slate-700">
              <span className="hidden text-sm text-slate-600 dark:text-slate-300 lg:inline">
                {user.username}
              </span>
              <Button variant="ghost" size="sm" onClick={logout}>
                Log out
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
